'use client';

import { useEffect, useRef } from 'react';
import { styled } from 'styled-components';

import { usePrefersReducedMotion } from '../_lib/usePrefersReducedMotion';
import { darkHomeTokens } from '../_design/tokens';

const Region = styled.div`
  position: relative;

  /* The cursor-trailing orb, resting high-centre until the cursor approaches. */
  &::before {
    background: radial-gradient(
      26rem circle at var(--spot-x, 50%) var(--spot-y, 18%),
      ${darkHomeTokens.colors.orbViolet},
      ${darkHomeTokens.colors.orbBlue} 34%,
      transparent 68%
    );
    content: '';
    inset: 0;
    /* Visible without JS; the script only sharpens it toward the cursor. */
    opacity: 0.7;
    pointer-events: none;
    position: absolute;
    transition: opacity ${darkHomeTokens.transition.duration.slow} ease;
    z-index: 0;
  }

  &.lit::before {
    opacity: 1;
  }

  /* Children sit above the orb. */
  > * {
    position: relative;
    z-index: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    &::before {
      background: radial-gradient(
        26rem circle at 50% 18%,
        ${darkHomeTokens.colors.orbViolet},
        ${darkHomeTokens.colors.orbBlue} 34%,
        transparent 68%
      );
      opacity: 0.85;
    }
  }
`;

/**
 * One orb that trails the cursor behind the closing CTA and the footer, so the
 * two read as one scene. The pointer is tracked at the window level so the orb
 * follows it across the boundary between them. Position is written as two
 * custom properties, no React state, eased per frame so it drifts rather than
 * snaps.
 */
const CtaGlow = ({ children }: { children: React.ReactNode }) => {
  const regionRef = useRef<HTMLDivElement | null>(null);
  const frame = useRef<number | null>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) return undefined;

    // The orb lerps from `current` toward `target` each frame so it trails the
    // pointer rather than sticking to it.
    let target: { x: number; y: number } | null = null;
    const current = { x: 0, y: 0 };
    let seeded = false;
    // Lower = looser / laggier follow.
    const EASE = 0.16;

    const step = () => {
      const region = regionRef.current;
      if (!region || !target) {
        frame.current = null;
        return;
      }
      if (!seeded) {
        // Jump to the first target so the orb doesn't sweep in from 0,0.
        current.x = target.x;
        current.y = target.y;
        seeded = true;
      } else {
        current.x += (target.x - current.x) * EASE;
        current.y += (target.y - current.y) * EASE;
      }
      region.style.setProperty('--spot-x', `${current.x}px`);
      region.style.setProperty('--spot-y', `${current.y}px`);

      const settled =
        Math.abs(target.x - current.x) < 0.5 &&
        Math.abs(target.y - current.y) < 0.5;
      frame.current = settled ? null : window.requestAnimationFrame(step);
    };

    const onPointerMove = (event: PointerEvent) => {
      const region = regionRef.current;
      if (!region) return;
      const rect = region.getBoundingClientRect();
      target = { x: event.clientX - rect.left, y: event.clientY - rect.top };
      // Lit while the cursor is within or just above the region.
      const near =
        event.clientY >= rect.top - rect.height * 0.4 &&
        event.clientY <= rect.bottom;
      region.classList.toggle('lit', near);
      if (frame.current === null)
        frame.current = window.requestAnimationFrame(step);
    };

    window.addEventListener('pointermove', onPointerMove, { passive: true });
    return () => {
      window.removeEventListener('pointermove', onPointerMove);
      if (frame.current !== null) window.cancelAnimationFrame(frame.current);
      /* MUST be reset: the pointer handler only schedules a frame when this is
         null, so an effect re-run (a reduced-motion toggle) would otherwise
         leave a cancelled id parked here and the orb would never move again. */
      frame.current = null;
    };
  }, [prefersReducedMotion]);

  return <Region ref={regionRef}>{children}</Region>;
};

export default CtaGlow;
